import { z } from "zod";

// PROJECT.md sec 9 + DESIGN.md sec 12: ED-aware minimums.
export const MIN_KCAL_TARGET = 1200;
export const MAX_KCAL_TARGET = 6000;

export const BiologicalSexSchema = z.enum(["male", "female"]);
export type BiologicalSex = z.infer<typeof BiologicalSexSchema>;

export const ActivityLevelSchema = z.enum(["sedentary", "light", "moderate", "high", "very-high"]);
export type ActivityLevel = z.infer<typeof ActivityLevelSchema>;

export const TargetsSchema = z.object({
  kcalTarget: z.number().int().min(800).max(MAX_KCAL_TARGET).nullable(),
  proteinTargetG: z.number().int().min(0).max(500).nullable(),
  fatTargetG: z.number().int().min(0).max(500).nullable(),
  carbsTargetG: z.number().int().min(0).max(1000).nullable(),
  weightKg: z.number().min(20).max(300).nullable(),
  acknowledgedLowTarget: z.boolean(),
  biologicalSex: BiologicalSexSchema.nullable(),
  heightCm: z.number().min(80).max(250).nullable(),
  birthYear: z.number().int().min(1900).max(2025).nullable(),
  activityLevel: ActivityLevelSchema.nullable(),
});
export type Targets = z.infer<typeof TargetsSchema>;

export const TargetsUpdateSchema = z.object({
  kcalTarget: z.number().int().min(800).max(MAX_KCAL_TARGET),
  proteinTargetG: z.number().int().min(0).max(500),
  fatTargetG: z.number().int().min(0).max(500),
  carbsTargetG: z.number().int().min(0).max(1000),
  weightKg: z.number().min(20).max(300).optional(),
  acknowledgedLowTarget: z.boolean().optional(),
  biologicalSex: BiologicalSexSchema.nullable().optional(),
  heightCm: z.number().min(80).max(250).nullable().optional(),
  birthYear: z.number().int().min(1900).max(2025).nullable().optional(),
  activityLevel: ActivityLevelSchema.nullable().optional(),
});
export type TargetsUpdate = z.infer<typeof TargetsUpdateSchema>;

export function isLowKcalTarget(kcal: number): boolean {
  return kcal < MIN_KCAL_TARGET;
}

// Mifflin-St Jeor BMR + activity multiplier. Returns null if any required
// input is missing — caller decides what to do (fall back to manual entry).
const ACTIVITY_MULTIPLIER: Record<ActivityLevel, number> = {
  sedentary: 1.2,
  light: 1.375,
  moderate: 1.55,
  high: 1.725,
  "very-high": 1.9,
};

export function calculateTdee(input: {
  biologicalSex: BiologicalSex | null;
  weightKg: number | null;
  heightCm: number | null;
  birthYear: number | null;
  activityLevel: ActivityLevel | null;
  currentYear?: number;
}): number | null {
  const { biologicalSex, weightKg, heightCm, birthYear, activityLevel } = input;
  if (!biologicalSex || !weightKg || !heightCm || !birthYear || !activityLevel) return null;
  const year = input.currentYear ?? new Date().getFullYear();
  const age = year - birthYear;
  if (age < 10 || age > 120) return null;
  const sexAdjustment = biologicalSex === "male" ? 5 : -161;
  const bmr = 10 * weightKg + 6.25 * heightCm - 5 * age + sexAdjustment;
  return Math.round(bmr * ACTIVITY_MULTIPLIER[activityLevel]);
}
